export type AgentType = 'supervisor' | 'micro';

export interface CategoryResponse {
  id: string;
  name: string;
}

export interface UpsertCategoryRequest {
  id?: string;
  name: string;
}

export interface UpsertManualRequest {
  id?: string;
  name: string;
  category: string;
  description: string;
  bestPracticeExecutionIds?: string[];
  manualMarkdown?: string;
}

export interface ManualResponse {
  id: string;
  name: string;
  category: string;
  description: string;
  bestPracticeExecutionIds?: string[];
  manualMarkdown: string;
  updatedAt: string;
}

export interface UpsertAgentPersonaRequest {
  id?: string;
  name: string;
  type: AgentType;
  category: string;
  description: string;
  parentId?: string;
  manualId?: string;
  requiredParams?: Record<string, unknown>;
  personaMarkdown?: string;
  manualMarkdown?: string;
}

export interface AgentPersonaResponse {
  id: string;
  name: string;
  type: AgentType;
  category: string;
  description: string;
  personaMarkdown: string;
  parentId?: string;
  manualId?: string;
  manualMarkdown?: string;
  requiredParams?: Record<string, unknown>;
  updatedAt: string;
}

export interface ManualHistoryResponse {
  versions: ManualResponse[];
}

export interface AgentPersonaHistoryResponse {
  versions: AgentPersonaResponse[];
}

export class AIAgentRegistryClient {
  private readonly headers: HeadersInit;

  constructor(
    private readonly apiUrl: string,
    apiKey: string,
  ) {
    this.headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-API-Key': apiKey,
    };
  }

  public async listCategories(): Promise<CategoryResponse[]> {
    const response = await fetch(`${this.apiUrl}/agent/category`, {
      method: 'GET',
      headers: this.headers,
      mode: 'cors',
    });
    if (!response.ok) throw new Error(`Failed to fetch categories: ${response.statusText}`);
    return response.json();
  }

  public async upsertCategory(request: UpsertCategoryRequest): Promise<void> {
    const response = await fetch(`${this.apiUrl}/agent/category`, {
      method: 'POST',
      headers: this.headers,
      mode: 'cors',
      body: JSON.stringify(request),
    });
    if (!response.ok) throw new Error(`Failed to upsert category: ${response.statusText}`);
  }

  public async deleteCategory(categoryId: string): Promise<void> {
    const response = await fetch(`${this.apiUrl}/agent/category/${categoryId}`, {
      method: 'DELETE',
      headers: this.headers,
      mode: 'cors',
    });
    if (!response.ok) throw new Error(`Failed to delete category: ${response.statusText}`);
  }

  public async listSupervisorAgents(): Promise<AgentPersonaResponse[]> {
    const response = await fetch(`${this.apiUrl}/agent/persona/supervisors`, {
      method: 'GET',
      headers: this.headers,
      mode: 'cors',
    });
    if (!response.ok) throw new Error(`Failed to fetch supervisors: ${response.statusText}`);
    return response.json();
  }

  public async listMicroAgents(supervisorId: string): Promise<AgentPersonaResponse[]> {
    const response = await fetch(`${this.apiUrl}/agent/persona/supervisors/${supervisorId}/microAgents`, {
      method: 'GET',
      headers: this.headers,
      mode: 'cors',
    });
    if (!response.ok) throw new Error(`Failed to fetch micro agents: ${response.statusText}`);
    return response.json();
  }

  public async getAgentPersona(agentId: string): Promise<AgentPersonaResponse> {
    const response = await fetch(`${this.apiUrl}/agent/persona/${agentId}`, {
      method: 'GET',
      headers: this.headers,
      mode: 'cors',
    });
    if (!response.ok) throw new Error(`Failed to fetch agent: ${response.statusText}`);
    return response.json();
  }

  public async upsertAgentPersona(request: UpsertAgentPersonaRequest): Promise<void> {
    const response = await fetch(`${this.apiUrl}/agent/persona`, {
      method: 'POST',
      headers: this.headers,
      mode: 'cors',
      body: JSON.stringify(request),
    });
    if (!response.ok) {
      let errorMessage = `Failed to upsert agent: ${response.statusText}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch {
        // Ignore JSON parse errors
      }
      throw new Error(errorMessage);
    }
  }

  public async deleteAgentPersona(agentId: string): Promise<void> {
    const response = await fetch(`${this.apiUrl}/agent/persona/${agentId}`, {
      method: 'DELETE',
      headers: this.headers,
      mode: 'cors',
    });
    if (!response.ok) throw new Error(`Failed to delete agent: ${response.statusText}`);
  }

  public async listManualsByCategory(category: string): Promise<ManualResponse[]> {
    const response = await fetch(`${this.apiUrl}/agent/manual/category/${category}`, {
      method: 'GET',
      headers: this.headers,
      mode: 'cors',
    });
    if (!response.ok) throw new Error(`Failed to fetch manuals: ${response.statusText}`);
    return response.json();
  }

  public async getManual(manualId: string): Promise<ManualResponse> {
    const response = await fetch(`${this.apiUrl}/agent/manual/${manualId}`, {
      method: 'GET',
      headers: this.headers,
      mode: 'cors',
    });
    if (!response.ok) throw new Error(`Failed to fetch manual: ${response.statusText}`);
    return response.json();
  }

  public async upsertManual(request: UpsertManualRequest): Promise<void> {
    const response = await fetch(`${this.apiUrl}/agent/manual`, {
      method: 'POST',
      headers: this.headers,
      mode: 'cors',
      body: JSON.stringify(request),
    });
    if (!response.ok) throw new Error(`Failed to upsert manual: ${response.statusText}`);
  }

  public async deleteManual(manualId: string): Promise<void> {
    const response = await fetch(`${this.apiUrl}/agent/manual/${manualId}`, {
      method: 'DELETE',
      headers: this.headers,
      mode: 'cors',
    });
    if (!response.ok) throw new Error(`Failed to delete manual: ${response.statusText}`);
  }

  public async getManualHistory(manualId: string): Promise<ManualHistoryResponse> {
    const response = await fetch(`${this.apiUrl}/agent/manual/${manualId}/history`, {
      method: 'GET',
      headers: this.headers,
      mode: 'cors',
    });
    if (!response.ok) throw new Error(`Failed to fetch manual history: ${response.statusText}`);
    return response.json();
  }

  public async getAgentPersonaHistory(agentId: string): Promise<AgentPersonaHistoryResponse> {
    const response = await fetch(`${this.apiUrl}/agent/persona/${agentId}/history`, {
      method: 'GET',
      headers: this.headers,
      mode: 'cors',
    });
    if (!response.ok) throw new Error(`Failed to fetch agent persona history: ${response.statusText}`);
    return response.json();
  }
}

